import React, { FunctionComponent, useCallback } from "react";
import { colors, ConfigProvider } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useAppDispatch, useAppSelector } from "@store/store";
import { Search } from "../Search/Search";
import { MobileMenu } from "../MobileMenu/MobileMenu";
import {
  selectLastMenuItem,
  selectPlanMenuItem,
} from "@store/menuItem/menuItemReducer";
import { setMenuItemAction } from "@store/menuItem/actions/setMenuItemAction";
import {
  selectClient,
  selectIsTripBuilderEnabled,
} from "@store/client/clientReducer";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { UserAuth } from "../UserAuth/UserAuth";
import { AiButton } from "@modules/components/buttons/AiButton/AiButton";
import { useTranslation } from "react-i18next";
import { useMatch } from "react-router";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { selectPlanItems } from "@store/plan/planReducer";
import { Text } from "@ui-kit/Text/Text";
import { selectIsHeaderVisible } from "@store/app/appReducer";

import "./Header.css";

interface HeaderProps {}

const HeaderCenterNavigation = () => {
  const planMenuItem = useAppSelector(selectPlanMenuItem);
  const lastMenuItem = useAppSelector(selectLastMenuItem);
  const isPlanScreen = useMatch("/plan");
  const { t } = useTranslation();
  const planItems = useAppSelector(selectPlanItems);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handlePress = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isPlanScreen) {
        navigate("/plan");
      } else {
        navigate(lastMenuItem!.analyticsPath);
        dispatch(setMenuItemAction(lastMenuItem!));
      }
    },
    [isPlanScreen, navigate, dispatch, lastMenuItem],
  );

  if (!planMenuItem) {
    return null;
  }

  return (
    <div className="header-center-navigation-component">
      <a
        className={`navigation-link${!isPlanScreen ? " active" : ""}`}
        style={{
          color: !isPlanScreen ? `${colors.primary60}` : undefined,
        }}
        onClick={handlePress}
        href="/">
        {t("shell.explore")}
      </a>
      <a
        className={`navigation-link${isPlanScreen ? " active" : ""}`}
        style={{
          color: isPlanScreen ? `${colors.primary60}` : undefined,
        }}
        onClick={handlePress}
        href={planMenuItem.analyticsPath}>
        {planMenuItem.name}
        {planItems.length > 0 && (
          <div className="plan-item-count" data-testid="plan-item-count">
            <Text variant="labelSmall" color={colors.textInvert}>
              {planItems.length}
            </Text>
          </div>
        )}
      </a>
    </div>
  );
};

const Header: FunctionComponent<HeaderProps> = () => {
  const { t } = useTranslation();
  const client = useAppSelector(selectClient);
  const isHeaderVisible = useAppSelector(selectIsHeaderVisible);
  const isTripBuilderEnabled = useAppSelector(selectIsTripBuilderEnabled);
  const { width } = useWindowSize();

  if (!isHeaderVisible) {
    return null;
  }

  return (
    <ConfigProvider type="client">
      <div
        className="header-component"
        style={{ backgroundColor: colors.primary60 }}>
        <div className="inner">
          <div className="header-left">
            <div className="logo-container">
              <img src={client.widgetLogoUrl} alt="Logo" height={40} />
            </div>
          </div>

          <div className="header-center">
            <HeaderCenterNavigation />
          </div>

          <div className="header-right">
            <div className="search-container">
              <Search />
            </div>

            {isTripBuilderEnabled && (
              <div className="trip-builder-button-container">
                <AiButton
                  label={
                    width > 1268
                      ? t("ai.tripBuilder.launch.helpMePlan")
                      : undefined
                  }
                  flex={false}
                />
              </div>
            )}

            <div className="menu-container">
              <MobileMenu />
            </div>

            <UserAuth />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export { Header };
